import { AxiosResponse } from 'axios';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { Twitter } from 'components/common/Twitter';
import { LdJsonBreadCrumbsResort, LdJsonSkiResort } from 'components/LdJson';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { Routes } from 'routes';

import { getResortSlug } from 'util/memcache';
import { SnowReportComments } from 'components/resort/SnowReportComments';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortFeaturedCam,
  fetchResortLastFHReport, fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortReviews,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortSnowReport,
} from 'api/resort/static';
import {
  IFirstHandReport,
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IReviewsGeneral,
} from 'api/resort/types';
import { IWebcamItem } from 'api/region/types';
import { ResortLastHandReport } from 'components/resort/ResortLastHandReport';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { Summary } from 'components/resort/Summary';
import { RecentSnowfall } from 'components/resort/RecentSnowfall';
import { ForecastedSnow } from 'components/resort/ForecastedSnow';
import { Terrain } from 'components/resort/Terrain';
import { AdditionalActivities } from 'components/resort/AdditionalActivities';
import { LiftTimes } from 'components/resort/LiftTimes';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { useFakeQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import { RegularAd } from 'googlead/components/RegularAd';
import { hasNordicStatsData, hasRangeData } from 'util/isEmpty';
import { useUnitContext } from 'components/UnitSwitch/context';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { DistanceType } from 'i18n/types';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { formatDateFromStr } from 'util/formatDate';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { IResortLayout } from './_layout';

import styles from './skireport.module.scss';

export type ResortSkiReportPageProps = {
  featured: IWebcamItem | null,
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  reviewsGeneral: IReviewsGeneral | null
  relatedNews: IRelatedNews | null,
  initials: {
    firstHand: IFirstHandReport | null,
  }
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-skireport-page';
export const TEMPLATE_ID = '2.2.1';
export const TEMPLATE_NAME = 'Resort Ski Report';

export default function ResortSkiReportPage({
  featured,
  fullResort: preFullResort,
  initials,
  pathInfo,
  reviewsGeneral,
  relatedNews: preRelatedNews,
}: ResortSkiReportPageProps) {
  const { distance } = useUnitContext();
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  /**
   * Refresh data with new one
   */
  const { current: fullResort, hasFreshData } = useFakeQueryWithInitialValue(
    ['resort-fetchResortSnowReport', pathInfo.uuid],
    async () => fetchResortSnowReport(pathInfo.uuid),
    preFullResort,
  );

  const { current: relatedNews, hasFreshData: hasFreshRelatedNews } = useFakeQueryWithInitialValue(
    ['resort-relatedNews', fullResort.uuid],
    async () => fetchResortRelatedNews(fullResort.uuid),
    preRelatedNews,
  );

  const {
    current: curFeatured,
    hasFreshData: hasFreshFeatured,
  } = useFakeQueryWithInitialValue(['resort-featuredcam', fullResort.uuid],
    async () => fetchResortFeaturedCam(fullResort.uuid),
    featured);

  const dt = useDistanceFormatter('cmOrInchShort');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const hasActivitiesData = hasNordicStatsData(fullResort.terrain?.nordic)
    || hasRangeData(fullResort.terrain?.parks)
    || hasRangeData(fullResort.terrain?.pipes)
    || hasRangeData(fullResort.terrain?.tubes);

  const resortCountry = fullResort?.region?.country_code;
  const isMetricUnits = distance === DistanceType.METRIC;

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.skiReport.seoTitle', { resort: fullResort.title })} />
          <BlockContent className={styles.seoText}>
            {m('resort.skiReport.seoText', {
              resort: fullResort.title,
              depth: dt([fullResort.depths?.base, fullResort.depths?.summit], '-'),
              base_depth_cm: dt(fullResort.depths?.base),
              top_depth_cm: dt(fullResort.depths?.summit),
              base_depth: dt(fullResort.depths?.base),
              top_depth: dt(fullResort.depths?.summit),
              unitMeasurement: '',
              lifts_open: fullResort.lifts?.open,
              num_lifts: fullResort.lifts?.total,
              created_dt: formatDateFromStr(fullResort.updatedAt),
            })}
          </BlockContent>
        </BoxWithShadow>
        <Summary
          lifts={fullResort.lifts}
          runs={fullResort.runs}
          runsKm={fullResort.terrain?.km}
          surface={fullResort.surfaceType}
          surfaceBase={fullResort.surfaceTypeBottom}
          isFresh={hasFreshData}
          resort={pathInfo}
          updated={fullResort.updatedAt}
          snow={fullResort.snow}
          status={fullResort.status}
          resortCountry={resortCountry}
          weather={fullResort?.currentWeather}
          isMetricUnits={isMetricUnits}
        />
        {fullResort.comment && <SnowReportComments isFresh={hasFreshData} comments={fullResort.comment} />}

        {fullResort.recent && (
          <RecentSnowfall
            isFresh={hasFreshData}
            resort={pathInfo}
            data={fullResort.recent}
            isMetricUnits={isMetricUnits}
          />
        )}
        {fullResort.forecast && (
          <ForecastedSnow
            isFresh={hasFreshData}
            resort={pathInfo}
            data={fullResort.forecast}
            isMetricUnits={isMetricUnits}
          />
        )}
        {!!curFeatured && (
        <FeaturedCamera
          isFresh={hasFreshFeatured || hasFreshData}
          cam={curFeatured}
          href={Routes.resortCams(pathInfo)}
        />
        )}
        <RegularAd />
        {fullResort.terrain && (
          <Terrain
            isFresh={hasFreshData}
            data={fullResort.terrain}
            pathInfo={pathInfo}
            resortCountry={resortCountry}
          />
        )}
        {hasActivitiesData && (
          <AdditionalActivities isFresh={hasFreshData} data={fullResort.terrain} />
        )}
        <ResortLastHandReport resort={pathInfo} initial={initials.firstHand} />
        <RegularAd />
        <LiftTimes isFresh={hasFreshData} schedule={fullResort.schedule} status={fullResort.status} />
        <PlanYourTrip
          resort={pathInfo}
          hasSkiRental={pathInfo.hasSkiRental}
          liftsUrl={fullResort.liftsUrl}
          rentalUrl={fullResort.rentalUrl}
          lessonsUrl={fullResort.lessonsUrl}
          isPartner={fullResort.isPartner}
          resortCountry={resortCountry}
        />
        <RegularAd />
      </Main>
      <Side>
        {fullResort.twitter && <Twitter feed={fullResort.twitter} />}
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh={hasFreshRelatedNews}
        />
        <SidebarAd />
      </Side>
      <LdJsonSkiResort
        schedule={fullResort.schedule}
        resort={fullResort}
        averageReviews={reviewsGeneral}
      />
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={fullResort.region}
        fn={Routes.resortSnowReport}
      />
    </>
  );
}

export const fetchProps = async ({ resort }:{ region: string, resort: string }): Promise<ResortSkiReportPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const reviewsGeneral = await fetchResortReviews(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid);
  const featured = await fetchResortFeaturedCam(uuid);

  return {
    type: 'resort',
    route: 'skireport',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    pathInfo: cache.data,
    reviewsGeneral,
    relatedNews,
    featured,
    initials: {
      firstHand: await fetchResortLastFHReport(uuid),
    },

    /**
         * Layout
         */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.SNOW_REPORT,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort skireport',
    hero: {
      title: 'resort.skiReport.heroTitle',
      showVideo: true,
    },
    shouldShare: true,
    meta: {
      description: 'Resort.skireport.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.skireport.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
  };
};

export const getResortSkiReportPageStaticProps = wrapStaticProps(fetchProps);
export const getResortSkiReportPageServerSideProps = wrapServerSideProps(fetchProps);
